import React, { useEffect, useRef, useState } from 'react';
import { getIn, useField, useFormikContext } from 'formik';
import { at } from 'lodash';
import { FormHelperText, Box, Typography, InputAdornment } from '@mui/material';
import { useParams } from 'react-router-dom';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { MuiFileInput } from 'mui-file-input';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { extractFilenameFromKey } from '../../../util/util';
import {
  isPreviewableImage,
  isUnsupportedImagePreview,
  useInView,
} from '../../../util/filePreviewUtils';
import { useResponsive } from '../../../hooks/ResponsiveProvider';
import ImagePreview from './ImagePreview';

const UploadDocumentField = (props) => {
  let { siteInitStep } = useParams();
  const {
    name,
    label,
    sectionKey,
    fieldKey,
    supportedFormats,
    fileSize,
    subSectionKey,
    parentKey,
    handleDownloadFile,
    setFieldValue,
    handleUploadFile,
    disabled,
    required,
  } = props;
  const { errors, touched } = useFormikContext();
  const [field, meta] = useField(name);
  const [error] = at(meta, 'error');
  const responsive = useResponsive() || {};
  const { isSmallScreen } = responsive;

  const [selectedFile, setSelectedFile] = useState(null);
  const [manualError, setManualError] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [containerRef, inView] = useInView();
  const lastFetchedRef = useRef(null);
  const localPreviewRef = useRef(false);

  const isImage = isPreviewableImage(fileName || field.value);
  const isUnsupportedPreview = isUnsupportedImagePreview(fileName || field.value);

  useEffect(() => {
    return () => {
      if (previewSrc && previewSrc.startsWith('blob:')) {
        URL.revokeObjectURL(previewSrc);
      }
    };
  }, [previewSrc]);

  useEffect(() => {
    if (!field.value) {
      if (!localPreviewRef.current) {
        setFileName(null);
        setPreviewSrc(null);
        setPreviewError(false);
        setPreviewLoading(false);
        lastFetchedRef.current = null;
      }
      return;
    }

    const _fName = extractFilenameFromKey(field.value);
    setFileName(_fName);

    if (localPreviewRef.current) {
      lastFetchedRef.current = field.value;
      return;
    }

    if (!isPreviewableImage(field.value)) {
      setPreviewSrc(null);
      setPreviewLoading(false);
      setPreviewError(false);
      return;
    }

    if (!inView || !handleDownloadFile) {
      return;
    }

    if (lastFetchedRef.current === field.value) {
      return;
    }

    lastFetchedRef.current = field.value;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(false);

    handleDownloadFile(
      field.value,
      _fName,
      (blob) => {
        if (cancelled) {
          return;
        }
        if (blob) {
          setPreviewSrc(URL.createObjectURL(blob));
          setPreviewError(false);
        } else {
          setPreviewSrc(null);
          setPreviewError(true);
        }
        setPreviewLoading(false);
      },
      { triggerDownload: false }
    );

    return () => {
      cancelled = true;
    };
    // handleDownloadFile is omitted to avoid refetch loops; lastFetchedRef dedupes by S3 key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, inView]);

  function _renderHelperText() {
    let helperError = error;
    if (manualError) {
      return <FormHelperText sx={{ color: '#d32f2f' }}>{manualError}</FormHelperText>;
    }
    if (!helperError) {
      helperError = parentKey && errors[parentKey] ? errors[parentKey][fieldKey + '_inputFile'] : errors[fieldKey + '_inputFile'];
      if (helperError) {
        return <FormHelperText sx={{ color: '#d32f2f' }}>{helperError}</FormHelperText>;
      }
    } else if (Boolean(getIn(touched, name))) {
      return <FormHelperText sx={{ color: '#d32f2f' }}>{helperError}</FormHelperText>;
    }
    return <FormHelperText sx={{ color: '#d32f2f' }}>{''}</FormHelperText>;
  }

  const resetPreviewState = () => {
    localPreviewRef.current = false;
    lastFetchedRef.current = null;
    setPreviewSrc(null);
    setPreviewError(false);
    setPreviewLoading(false);
    setSelectedFile(null);
    setFileName(null);
  };

  const handleDownload = () => {
    if (!handleDownloadFile || !field.value) {
      return;
    }
    handleDownloadFile(field.value, fileName);
  };

  const getExtension = (file) => {
    if (!file?.name) {
      return '';
    }
    return file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
  };

  const handleFileChange = async (e) => {
    if (!e) {
      resetPreviewState();
      setFieldValue(field.name, '');
      setManualError(null);
      return;
    }

    const allowedFormats = Array.isArray(supportedFormats)
      ? supportedFormats.map((format) => String(format).toLowerCase())
      : null;
    const extension = getExtension(e);

    if (fileSize && fileSize < e.size) {
      resetPreviewState();
      setFieldValue(field.name, '');
      setManualError('File is too large');
      return;
    }

    if (allowedFormats && !allowedFormats.includes(extension)) {
      resetPreviewState();
      setFieldValue(field.name, '');
      setManualError('Unsupported Format');
      return;
    }

    setManualError(null);
    setSelectedFile(e);
    setFileName(e.name);
    localPreviewRef.current = true;
    lastFetchedRef.current = null;
    setPreviewError(false);

    if (isPreviewableImage(e.name)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewSrc(reader.result);
        setPreviewLoading(false);
      };
      reader.onerror = () => {
        setPreviewSrc(null);
        setPreviewError(true);
        setPreviewLoading(false);
      };
      reader.readAsDataURL(e);
    } else {
      setPreviewSrc(null);
      setPreviewLoading(false);
    }

    if (!handleUploadFile) {
      return;
    }

    const keys = {
      stepKey: siteInitStep,
      sectionKey: sectionKey,
      subSectionKey: subSectionKey,
      categoryKey: parentKey ? parentKey : '',
      fieldKey: fieldKey,
      file: e,
    };

    try {
      await handleUploadFile(keys, field.name, setFieldValue);
    } catch (uploadError) {
      resetPreviewState();
      setFieldValue(field.name, '');
      setManualError('Failed to upload file');
    }
  };

  const showPicker = !disabled || !fileName;
  const showDownload = Boolean(fileName && handleDownloadFile && field.value);

  return (
    <Box ref={containerRef} sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <Typography sx={{ color: '#5b5b5b', fontWeight: 'medium' }} variant="subtitle1" gutterBottom>
        {label}
        {label && required && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
      </Typography>
      {disabled && !fileName ? (
        <Typography variant="body2" color="text.secondary">
          No file uploaded
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isSmallScreen ? 'column' : 'row',
            alignItems: isSmallScreen ? 'stretch' : 'center',
            gap: isSmallScreen ? 1 : 0,
            minWidth: 0,
            width: '100%',
          }}
        >
          {showPicker && (
            <MuiFileInput
              disabled={!!disabled}
              sx={{
                flex: 1,
                minWidth: 0,
                width: '100%',
                '& .MuiFileInput-placeholder': {
                  overflow: 'hidden',
                },
              }}
              placeholder={fileName ? fileName : 'Click here to insert a file'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachFileIcon />
                  </InputAdornment>
                ),
              }}
              value={selectedFile}
              name={name + '_inputFile'}
              hideSizeText={true}
              onChange={handleFileChange}
            />
          )}
          {disabled && fileName && (
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fileName}
            </Typography>
          )}
          {showDownload && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', paddingLeft: isSmallScreen ? 0 : 2, cursor: 'pointer' }}
              onClick={handleDownload}
            >
              <FileDownloadOutlinedIcon sx={{ color: '#3C6FEB', marginLeft: isSmallScreen ? 0 : 1 }} />
            </Box>
          )}
        </Box>
      )}
      {(isImage || previewLoading || previewError || isUnsupportedPreview) && (
        <ImagePreview
          src={previewSrc}
          alt={fileName || label || 'Uploaded image'}
          fileName={fileName}
          loading={previewLoading}
          error={previewError}
          unavailable={isUnsupportedPreview && !previewSrc}
        />
      )}
      <input type="hidden" {...field} />
      {_renderHelperText()}
    </Box>
  );
};

export default React.memo(UploadDocumentField);
